import { useEffect, useState } from 'react';

import { getI18n } from 'react-i18next';
import { fetchApi } from 'utils/fetchApi';

import { useUserOrganization } from '~/features/organizations/hooks/useUserOrganization';

// 👇 The Look ID for the Learning
const LOOKER_LEARNING_BY_LANGUAGE = {
  EN: 297,
  FR: 301,
  DE: 300,
  IT: 302,
  ES: 303,
  PT: 304,
};
// 👇 The default Look ID for the Learning
let LOOKER_LEARNING_REPORT_ID = LOOKER_LEARNING_BY_LANGUAGE['EN'];

export type LearningState = {
  looker: {
    loading: boolean;
    url: string | null;
    error?: string;
  };
};
const initialState: LearningState = {
  looker: {
    loading: false,
    url: null,
  },
};

/**
 * Handle business logic related to Learning Paths
 *
 * @returns LearningState
 */
export function useLearning() {
  const { language } = getI18n();
  const { organizationData, isLoading, error } = useUserOrganization();
  const [looker, setLooker] = useState<LearningState['looker']>(initialState.looker);
  const LANG_KEY = language.split('_')[0].toUpperCase();

  if (LOOKER_LEARNING_BY_LANGUAGE[LANG_KEY as keyof typeof LOOKER_LEARNING_BY_LANGUAGE]) {
    LOOKER_LEARNING_REPORT_ID = LOOKER_LEARNING_BY_LANGUAGE[LANG_KEY as keyof typeof LOOKER_LEARNING_BY_LANGUAGE];
  }

  useEffect(() => {
    let stale = false;
    async function fetchLookerURL(language: string) {
      setLooker({
        loading: true,
        url: null,
      });
      try {
        const result = await fetchApi(`/api/lookerDashboardUrlById?lang=${language}&id=${LOOKER_LEARNING_REPORT_ID}`, {
          method: 'GET',
        });
        const data = (await result.json()) as { url: string };
        if (!stale) {
          setLooker({
            loading: false,
            url: data.url,
          });
        }
      } catch (error) {
        console.error(`Failed to fetch Looker URL: ${error}`);
        if (!stale) {
          setLooker({
            loading: false,
            url: null,
            error: String(error),
          });
        }
      }
    }
    fetchLookerURL(language);

    return () => {
      stale = true;
    };
  }, [language]);

  return { org: organizationData, looker, isLoading, error };
}
